import type { Device, ProvisioningPreview } from '@arcnet/contracts';
import { Badge } from '../../components/ui/Primitives.js';

/**
 * The parts of provisioning that two screens both need.
 *
 * The account-wide Provisioning page answers "which handsets can fetch a
 * configuration right now"; the tab on an extension answers the same question
 * for the one phone in front of you. They are different questions at different
 * scales, but the ANSWER has to be identical -- a window that reads "open · 20
 * min left" on one screen and "shut" on the other is worse than either.
 *
 * So the vocabulary lives here: what a vendor is called, what the three window
 * states are, and what a generated file looks like. Extracted when the extension
 * tab was built rather than copied, because the copy is how they drift.
 */

export const VENDOR_LABEL: Record<string, string> = {
  yealink: 'Yealink', fanvil: 'Fanvil', grandstream: 'Grandstream',
  polycom: 'Poly', snom: 'Snom', cisco: 'Cisco', generic: 'Other',
};

export const windowOpen = (d: Device): boolean =>
  !!d.provisioning_open_until && Date.parse(d.provisioning_open_until) > Date.now();

/**
 * Open, or pinned, or shut -- the three states the endpoint actually has: a
 * window anyone may fetch through, an address the handset has already used and
 * may keep using, or nothing (modules/provisioning/routes.ts).
 */
export function Window({ device }: { device: Device }) {
  if (windowOpen(device)) {
    const mins = Math.max(1, Math.round((Date.parse(device.provisioning_open_until!) - Date.now()) / 60_000));
    return <Badge tone="warn">open · {mins} min left</Badge>;
  }
  if (device.address_pinned) {
    return <span className="arc-subtle" title="This handset may re-fetch from the address it already used.">its own address</span>;
  }
  return <span className="arc-subtle">shut</span>;
}

/** The file itself, as the phone will receive it. */
export function Preview({ data, canReveal, onReveal }: {
  data: ProvisioningPreview; canReveal: boolean; onReveal: () => void;
}) {
  return (
    <div className="arc-stack" style={{ gap: 10 }}>
      <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <Badge tone={data.generates_config ? 'ok' : 'muted'}>
          {data.generates_config ? `${data.vendor_label} configuration` : 'settings to enter by hand'}
        </Badge>
        {data.skipped_lines > 0 && (
          <Badge tone="warn">
            {data.skipped_lines} line{data.skipped_lines === 1 ? '' : 's'} left out — no recoverable password
          </Badge>
        )}
        <div className="arc-spacer" />
        {!data.password_shown && canReveal && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={onReveal}>Show password</button>
        )}
      </div>

      {data.files.map((f) => (
        <div key={f.name}>
          <div className="arc-row arc-subtle" style={{ fontSize: 11.5, marginBottom: 4 }}>
            <span className="arc-mono">{f.name}</span>
            <div className="arc-spacer" />
            <span>{f.content_type}</span>
          </div>
          <pre className="arc-mono" style={{
            margin: 0, padding: 10, fontSize: 11.5, lineHeight: 1.5,
            background: 'var(--arc-surface-3)', borderRadius: 'var(--arc-r-sm)',
            maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>{f.body}</pre>
        </div>
      ))}

      <span className="arc-subtle" style={{ fontSize: 11.5 }}>
        Generated by the same code that answers the phone.
        {data.password_shown ? ' This copy contains the SIP password.' : ' The password is hidden.'}
      </span>
    </div>
  );
}
